/* eslint-disable max-lines */
import { useEffect, useState, useCallback } from 'react'
import {
  VStack,
  HStack,
  useDisclosure,
  Tag,
  Button,
  Checkbox,
  Tooltip,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  useMediaQuery,
  Text
} from '@chakra-ui/react'
import { useStores } from 'hooks/useStores'
import get from 'lodash/get'
import isNumber from 'lodash/isNumber'
import { observer } from 'mobx-react'
import qs from 'qs'
import Highlighter from 'react-highlight-words'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import { IDropdown } from 'types/common/select'
import { IEsGlobalSearchItem } from 'types/eslasticSearch'
import { archiveCollectionById, shareCollectionsToGroups } from 'API/collection'
import { shareProcessesToGroups } from 'API/groupProcesses'
import { archiveProcessById } from 'API/process'
import { addSearchHistory } from 'API/search'
import { ITableHeader, EAlignEnum, IPagination } from 'components/CkTable'
import CkPagination from 'components/CkPagination'
import { IItemData } from './components/SearchResultsList'
import ControlledShareWithGroupsModal from 'components/ControlledShareWithGroupsModal'
import Dropdown from 'components/Dropdown'
import GlobalSpinner from 'components/GlobalSpinner'
import ModalConfirm from 'components/ModalConfirm'
import ShareProcessWithGroupsModal from 'components/ShareProcessWithGroupsModal'
import ShareWithGroupsDialog from 'components/ShareWithGroupsDialog'
import SvgIcon from 'components/SvgIcon'
import SearchPageHeader from 'components/Topbar/components/SearchPageHeader'
import { EEsGlobalSearchCollection } from 'constants/elasticSearch'
import { AuthRoleIdEnum } from 'constants/user'
import { ICollection } from 'interfaces/collection'
import { IGroupMemberPermissionId } from 'interfaces/group'
import { IProcessWithRelations } from 'interfaces/process'
import { IStepWithRelations } from 'interfaces/step'
import IconBuilder from 'pages/IconBuilderPage/components/IconBuilder'
import routes from 'routes'
import CollectionIcon from '../../../assets/icons/collection-icon.svg?react'
import IconDetail from '../../../assets/icons/info.svg?react'
import { IProcess } from '../../../interfaces/process'
import { getValidArray } from '../../../utils/common'
import CollectionOverview from '../CollectionsPage/CollectionOverview'
import ProcedureIcon from '../Common/ProcedureIcon'
import ProcessSummary from '../Common/ProcessSummary'
import EmptyResult from './components/EmptyResult'
import UserFeedback from './components/UserFeedback'
import SearchResultsList from './components/SearchResultsList'
import {
  findFullWordChunks,
  isUserHavePermissionForProcess,
  updateViewOnlyItemsAfterSelectAll,
  isUserHavePermissionForCollection,
  addAllStepsOfProcessToCommonLibrary
} from './utils'
import styles from './searchPage.module.scss'
import colors from 'theme/colors.theme'
import CopyProcessModal from '../ProcessDetailPage/components/CopyProcessModal'

export interface ISearchPageQuery {
  searchText?: string
  page?: number
}


const SearchPage = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const query: ISearchPageQuery = qs.parse(location.search, { ignoreQueryPrefix: true })

  const searchText: string = query?.searchText ?? ''
  const page: number = query?.page ?? 1
  const { isOpen: isOpenProcess, onOpen: onOpenProcess, onClose: onCloseProcess } = useDisclosure()
  const { isOpen: isOpenCollection, onOpen: onOpenCollection, onClose: onCloseCollection } = useDisclosure()
  const {
    isOpen: isOpenConfirmAddToCommonLibrary,
    onOpen: onOpenConfirmAddToCommonLibrary,
    onClose: onCloseConfirmAddToCommonLibrary
  } = useDisclosure()
  const { searchStore, processStore, collectionStore, authStore, groupStore } = useStores()
  const { selectedProcess } = processStore
  const { userDetail } = authStore
  const [isMobile] = useMediaQuery('(max-width: 768px)')
  
  // Add null checks to prevent React error #300
  if (!userDetail) {
    return <GlobalSpinner />
  }
  
  const currentUserId = userDetail.id
  const [selectedCollectionId, setSelectedCollectionId] = useState(0)
  const [selectedProcessIdList, setSelectedProcessIdList] = useState<number[]>([])
  const [selectedCollectionIdList, setSelectedCollectionIdList] = useState<number[]>([])
  const [isOpenShareProcessToGroupDialog, setOpenShareProcessToGroupDialog] = useState(false)
  const [isOpenShareCollectionToGroupDialog, setOpenShareCollectionToGroupDialog] = useState(false)
  const [isOpenBulkShareModal, setOpenBulkShareModal] = useState(false)
  const { totalResults, searchResults, size, isSearching, isManageMode, searchFilter } = searchStore
  const [isOpenArchiveModal, setIsOpenArchiveModal] = useState(false)
  const [selectedGroupIds, setSelectedGroupIds] = useState<number[]>([])
  const [selectedViewOnlyProcessIds, setSelectedViewOnlyProcessIds] = useState<number[]>([])
  const [selectedViewOnlyCollectionIds, setSelectedViewOnlyCollectionIds] = useState<number[]>([])
  const totalSelectedItems = selectedCollectionIdList.length + selectedProcessIdList.length
  const isBasicUser = userDetail.authRoleId === AuthRoleIdEnum.BASIC_USER
  const isSelectedViewOnlyItems = !!(selectedViewOnlyProcessIds?.length || selectedViewOnlyCollectionIds?.length)
  const {
    isOpen: isOpenCopyProcessModal,
    onOpen: openCopyProcessModal,
    onClose: closeCopyProcessModal
  } = useDisclosure()

  async function handleSelectItem(item: IEsGlobalSearchItem) {
    const id = isNumber(+(item?.correlatingId ?? '')) ? +(item?.correlatingId ?? '') : 0
    if (!id) return
    if (item?.collectionName === EEsGlobalSearchCollection.PROCESS) {
      let newArray: number[] = [...selectedProcessIdList]
      if (newArray.indexOf(id) !== -1) {
        newArray.splice(newArray.indexOf(id), 1)
        setSelectedProcessIdList(newArray)
        setSelectedViewOnlyProcessIds(selectedViewOnlyProcessIds.filter(processId => processId !== id))
      } else {
        setSelectedProcessIdList([...newArray, id])
        if (isBasicUser) {
          const { accessPermission, editorPermission } = await isUserHavePermissionForProcess(
            currentUserId,
            isBasicUser,
            id
          )
          if (!editorPermission && !selectedViewOnlyProcessIds.includes(id)) {
            setSelectedViewOnlyProcessIds([...selectedViewOnlyProcessIds, id])
          }
        }
      }
      processStore.setSelectedProcessId(id)
    }

    if (item?.collectionName === EEsGlobalSearchCollection.COLLECTION) {
      let newArray: number[] = [...selectedCollectionIdList]
      if (newArray.indexOf(id) !== -1) {
        newArray.splice(newArray.indexOf(id), 1)
        setSelectedCollectionIdList(newArray)
        setSelectedViewOnlyCollectionIds(selectedViewOnlyCollectionIds.filter(collectionId => collectionId !== id))
      } else {
        setSelectedCollectionIdList([...newArray, id])
        if (isBasicUser) {
          const canEdit = await isUserHavePermissionForCollection(currentUserId, isBasicUser, id)
          if (!canEdit && !selectedViewOnlyCollectionIds.includes(id)) {
            setSelectedViewOnlyCollectionIds([...selectedViewOnlyCollectionIds, id])
          }
        }
      }
    }
  }

  async function handleArchiveItems() {
    try {
      if (selectedProcessIdList?.length) {
        await Promise.all([...getValidArray(selectedProcessIdList)].map(id => archiveProcessById(id)))
      }
      if (selectedCollectionIdList?.length) {
        await Promise.all([...getValidArray(selectedCollectionIdList)].map(id => archiveCollectionById(id)))
      }
      searchStore.search()
      setIsOpenArchiveModal(false)
      setSelectedProcessIdList([])
      setSelectedCollectionIdList([])
      toast.success('Archived items successfully')
    } catch (error: any) {
      toast.error('Archived items failed')
    }
  }

  function selectAndOpenShareModal(item: IEsGlobalSearchItem) {
    if (item?.collectionName === EEsGlobalSearchCollection.PROCESS) {
      processStore.setSelectedProcessId(item?.correlatingId ?? 0)
      setOpenShareProcessToGroupDialog(true)
    }
    if (item?.collectionName === EEsGlobalSearchCollection.COLLECTION) {
      collectionStore.getCollectDetail(item?.correlatingId ?? 0)
      setOpenShareCollectionToGroupDialog(true)
    }
  }

  function handleSelectAllItems() {
    const newProcessIdList: number[] = []
    const newCollectionIdList: number[] = []

    if (selectedProcessIdList?.length + selectedCollectionIdList?.length === searchResults?.length) {
      setSelectedProcessIdList([])
      setSelectedCollectionIdList([])
      return
    }

    searchResults.forEach(item => {
      const id = isNumber(+(item?.correlatingId ?? '')) ? +(item?.correlatingId ?? '') : 0
      if (!id) return
      if (item?.collectionName === EEsGlobalSearchCollection.PROCESS) {
        newProcessIdList.push(id)
      }

      if (item?.collectionName === EEsGlobalSearchCollection.COLLECTION) {
        newCollectionIdList.push(id)
      }
    })
    setSelectedProcessIdList(newProcessIdList)
    setSelectedCollectionIdList(newCollectionIdList)
    updateViewOnlyItemsAfterSelectAll(currentUserId, searchResults, setSelectedViewOnlyProcessIds, isBasicUser)
  }


  const clearSelectedItems = useCallback(() => {
    setSelectedProcessIdList([])
    setSelectedCollectionIdList([])
    setSelectedViewOnlyProcessIds([])
  }, [])

  function gotoPage(newPage: number): void {
    navigate(`${routes.search.value}?searchText=${searchText}&page=${newPage}`)
  }

  const pagination: IPagination = {
    gotoPage,
    pageIndex: Number(page),
    tableLength: totalResults
  }

  function handleOpenProcessModal(item: IProcessWithRelations): void {
    processStore.setSelectedProcess(item)
    processStore.setSelectedProcessId(item?.id ?? 0)
    onOpenProcess()
  }

  function handleOpenCollectionModal(item: ICollection): void {
    setSelectedCollectionId(item?.id ?? 0)
    onOpenCollection()
  }

  function handleChangePageSize(newPageSize: number): void {
    searchStore.changeSize(newPageSize)
    searchStore.search()
    gotoPage(1)
  }

  const setPageSize = (newPageSize: number): void => {
    handleChangePageSize(newPageSize)
  }

  async function onSubmitBulkShare() {
    try {
      if (selectedProcessIdList?.length) {
        await shareProcessesToGroups(selectedGroupIds, selectedProcessIdList)
      }
      if (selectedCollectionIdList?.length) {
        await shareCollectionsToGroups(selectedGroupIds, selectedCollectionIdList)
      }
      searchStore.search()
      setSelectedGroupIds([])
      setSelectedProcessIdList([])
      setSelectedCollectionIdList([])
      setOpenBulkShareModal(false)
      toast.success('Share items successfully')
    } catch (error: any) {
      toast.error('Share items failed')
    }
  }

  const handleItemClick = (isProcess: boolean, item: IEsGlobalSearchItem, index: number) => {
    if (isProcess) {
      searchText &&
        addSearchHistory({
          keyword: searchText,
          totalResult: totalResults,
          clickedIndex: (page - 1) * size + index + 1,
          userId: currentUserId,
          processId: item?.correlatingId ?? 0
        })
      navigate(routes.processes.processId.value(String(item?.correlatingId ?? '')))
    } else {
      searchText &&
        addSearchHistory({
          keyword: searchText,
          totalResult: totalResults,
          clickedIndex: (page - 1) * size + index + 1,
          userId: currentUserId,
          processId: item?.correlatingId ?? 0
        })
      navigate(routes.collections.collectionId.value(String(item?.correlatingId ?? '')))
    }
  }

  function getHeaderList(): ITableHeader[] {
    return [
      {
        Header: 'Name',
        accessor: 'name',
        align: EAlignEnum.LEFT
      }
    ]
  }

  const dataInTable: IItemData[] = getValidArray<IEsGlobalSearchItem>(searchResults).map((item, index) => {
    const isProcess: boolean = item?.collectionName === EEsGlobalSearchCollection.PROCESS
    const title: string = item.correlatingData?.name ?? ''
    const subSearchSource = item?.highlight?.subSearchSource?.[0] ?? ''
    const searchSource = (item?.highlight?.searchSource?.[0] ?? '') + subSearchSource
    const nameHightlight: string = item?.highlight?.name?.[0] ?? ''
    const searchSourceWithoutEm: string = searchSource.replace(/<[^>]*>?/gm, '')
    const nameString = nameHightlight.split('<em>').map((i: string) => i.split('</em>')[0])
    const searchString: string[] = searchSource.split('<em>').map((i: string) => i.split('</em>')[0])
    const searchWords: string[] = searchString.length > 1 ? searchString.slice(1) : []
    searchWords.unshift(searchText)
    const nameWords: string[] = nameString.length > 1 ? nameString.slice(1) : []

    const id = +(item?.correlatingId ?? '')
    const steps: IStepWithRelations[] = get(item, 'correlatingData.steps', [])
    const editorGroupIds = getValidArray(groupStore.groupMembers)
      .filter(groupMember => (groupMember?.permissionId ?? 0) < IGroupMemberPermissionId.VIEWER)
      .map(groupMember => groupMember?.groupId)
    const processCollectionGroupIds: number[] = []

    if (isProcess) {
      const processCollections: ICollection[] = get(item, 'correlatingData.collections', [])
      processCollectionGroupIds.push(
        ...getValidArray(processCollections).flatMap(collection =>
          getValidArray(collection.groups).map(group => group.id ?? 0)
        )
      )
    } else {
      processCollectionGroupIds.push(
        ...getValidArray((item?.correlatingData as ICollection)?.groups).map(group => group.id ?? 0)
      )
    }

    const isEditorValue =
      !isBasicUser ||
      getValidArray(editorGroupIds).some(
        groupId => getValidArray(item.groupIds).includes(groupId) || processCollectionGroupIds.includes(groupId)
      )

    return {
      id: item?.correlatingId ?? index,
      title: (
        <VStack spacing={1} alignItems="flex-start" width="100%" maxWidth="100%">
          <Highlighter
            className={styles.title}
            highlightClassName={styles.highlight}
            searchWords={[...nameWords, ...searchWords]}
            autoEscape
            caseSensitive
            textToHighlight={`${item?.correlatingId ?? ''} | ${title}`}
            findChunks={findFullWordChunks}
            onClick={() => handleItemClick(isProcess, item, index)}
          />
        </VStack>
      ),
      subtitle: (() => {
  
        if (!searchText || searchText.trim() === '') {
          if (isProcess) {
            const sortedSteps = getValidArray(steps).slice().sort((a, b) => (a.position ?? 0) - (b.position ?? 0))
            const firstTwoSteps = sortedSteps.slice(0, 2)
            if (firstTwoSteps.length > 0) {
              const stepNames = firstTwoSteps
                .map((step, idx) => {
                  const stepName = step.name ?? ''
                  return stepName ? `${idx + 1}. ${stepName}` : ''
                })
                .filter(Boolean)
              const hasMoreSteps = sortedSteps.length > 2
              if (stepNames.length > 0) {
                return (
                  <VStack spacing={0} alignItems="flex-start" onClick={() => handleItemClick(isProcess, item, index)} cursor="pointer">
                    {stepNames.map((stepName, idx) => (
                      <Text key={idx} color="gray.600" fontSize="14px">
                        {stepName}{idx === 1 && hasMoreSteps ? '...' : ''}
                      </Text>
                    ))}
                  </VStack>
                )
              }
            }
            return undefined
          } else {
            const collection = item?.correlatingData as ICollection
            const overview = collection?.overview ?? ''
            if (overview) {
              return (
                <Text color="gray.600" fontSize="14px" onClick={() => handleItemClick(isProcess, item, index)} cursor="pointer">
                  {overview}
                </Text>
              )
            }
            return undefined
          }
        }
        
        return !(searchSourceWithoutEm.includes(title) && title.includes(searchWords.join(' '))) ? (
          <Highlighter
            highlightClassName={styles.highlight}
            searchWords={searchWords}
            autoEscape
            caseSensitive
            findChunks={findFullWordChunks}
            textToHighlight={searchSourceWithoutEm.replace(title, '')}
            onClick={() => handleItemClick(isProcess, item, index)}
          />
        ) : undefined
      })(),
      leadingIcon: (
        <>
          {isProcess ? (
            (item?.correlatingData as IProcessWithRelations)?.documentType?.iconBuilder ? (
              <IconBuilder icon={(item?.correlatingData as IProcessWithRelations)?.documentType?.iconBuilder} size={24} isActive />
            ) : (
              <ProcedureIcon procedureIcon={(item?.correlatingData as IProcess)?.procedureIcon} size={24} />
            )
          ) : (
            <CollectionIcon />
          )}
        </> 
      ),
      isSelected: isProcess ? selectedProcessIdList.includes(id) : selectedCollectionIdList.includes(id),
      isEditor: isEditorValue,
      originalItem: item,
      onToggleSelect: () => handleSelectItem(item),
      onClick: () => handleItemClick(isProcess, item, index),
      onQuickView: () => {
        isProcess
          ? handleOpenProcessModal(item.correlatingData as IProcessWithRelations)
          : handleOpenCollectionModal(item.correlatingData as ICollection)
      },
      onShare: () => selectAndOpenShareModal(item),
      onArchive: () => {
        clearSelectedItems()
        handleSelectItem(item)
        setIsOpenArchiveModal(true)
      },
      onMakeCopy: isProcess && isEditorValue ? () => {
        processStore.setSelectedProcess(item.correlatingData as IProcessWithRelations)
        openCopyProcessModal()
      } : undefined,
      canShowShare: !!(item as { isPublished?: boolean })?.isPublished,
      canShowMakeCopy: isProcess && isEditorValue,
      canShowArchive: true
    }
  })


  useEffect(() => {
    if (searchText !== 'false') {
      searchStore.changeSearchText(searchText)
      clearSelectedItems()
    }
  }, [searchText, searchStore, clearSelectedItems])

  useEffect(() => {
    if (page) {
      clearSelectedItems()
      searchStore.changePage(page)
    }
  }, [page, searchStore, clearSelectedItems])

  useEffect(() => {
    return () => {
      searchStore.clearSearchResults()
      searchStore.setManageMode(false)
      searchStore.clearSearchFilter()
      clearSelectedItems()
    }
  }, [searchStore, clearSelectedItems])

  return (
    <VStack spacing={{ base: 0, md: 6 }} padding={0} alignItems="center">
      <HStack
        width="100%"
        maxWidth="1320px"
        justifyContent="space-between"
        alignItems="flex-start"
        marginBottom={{ base: 2, md: 6 }}
        marginTop={'80px !important'}
        hidden={getValidArray(searchResults).length === 0}
      >
        <HStack width="full" justify="space-between" spacing={2} height={{ base: 10, md: 8 }}>
          <SearchPageHeader />
        </HStack>
      </HStack>

      <VStack
        maxWidth="1320px"
        marginTop={'0px !important'}
        spacing={3}
        width="full"
        hidden={getValidArray(searchResults).length === 0}
      >
        <SearchResultsList
          tableData={dataInTable}
          headerList={getHeaderList()}
          showHeader={!isMobile}
          isManageMode={isManageMode}
          canManageActions={!isBasicUser}
          totalSelectedItems={totalSelectedItems}
          totalItems={searchResults.length}
          onSelectAll={handleSelectAllItems}
          bulkActions={[
            {
              label: 'Share',
              iconName: 'ic_share',
              onClick: () => setOpenBulkShareModal(true),
              disabled: !totalSelectedItems || !!isSelectedViewOnlyItems
            },
            {
              label: 'Archive',
              iconName: 'archive',
              onClick: () => setIsOpenArchiveModal(true),
              disabled: !totalSelectedItems || !!isSelectedViewOnlyItems,
              tooltip: 'You don\'t have permission to archive some of the items you have selected'
            }
          ]}
        />
        {getValidArray(searchResults).length > 0 && (
          <HStack width="full" justifyContent="center" paddingY={4}>
            <CkPagination pagination={pagination} pageSize={size} setPageSize={setPageSize} />
          </HStack>
        )}
      </VStack>
      {isSearching ? (
        <GlobalSpinner />
      ) : (
        getValidArray(searchResults)?.length === 0 && (
          <VStack width="full" spacing={8} marginTop="72px !important">
            <EmptyResult />
            <UserFeedback />
          </VStack>
        )
      )}
      <ProcessSummary isOpen={isOpenProcess} onClose={onCloseProcess} />
      <CollectionOverview
        collectionId={selectedCollectionId}
        isCentered
        toggle={isOpenCollection ? onCloseCollection : onOpenCollection}
        isOpen={isOpenCollection}
      />
      {isOpenCopyProcessModal && selectedProcess?.id && (
        <CopyProcessModal
          isOpen={isOpenCopyProcessModal}
          onClose={closeCopyProcessModal}
          processId={selectedProcess?.id}
        />
      )}

      {isOpenShareProcessToGroupDialog && (
        <ShareProcessWithGroupsModal
          refetch={() => {
            searchStore.search()
            setSelectedProcessIdList([])
          }}
          isOpen={isOpenShareProcessToGroupDialog}
          toggle={() => setOpenShareProcessToGroupDialog(!isOpenShareProcessToGroupDialog)}
          isBasicUser={isBasicUser}
        />
      )}
      {isOpenShareCollectionToGroupDialog && (
        <ShareWithGroupsDialog
          refetch={() => {
            searchStore.search()
            setSelectedCollectionIdList([])
          }}
          isOpen={isOpenShareCollectionToGroupDialog}
          toggle={() => {
            if (isOpenShareCollectionToGroupDialog) {
              processStore.setNewProcessGroupIds([])
              processStore.setSelectedProcessId(0)
            }

            setOpenShareCollectionToGroupDialog(!isOpenShareCollectionToGroupDialog)
          }}
          isEdit
          isBasicUser={isBasicUser}
        />
      )}
      {isOpenBulkShareModal && (
        <ControlledShareWithGroupsModal
          isOpen={isOpenBulkShareModal}
          selectedGroupIds={selectedGroupIds}
          setSelectedGroupIds={setSelectedGroupIds}
          toggle={() => setOpenBulkShareModal(!isOpenBulkShareModal)}
          onSubmit={onSubmitBulkShare}
          isBasicUser={isBasicUser}
        />
      )}
      {isOpenArchiveModal && (
        <ModalConfirm
          title="Archive Items?"
          content="You can find archived items in the Archive page."
          isOpen={isOpenArchiveModal}
          confirmAction="Archive"
          toggle={() => setIsOpenArchiveModal(!isOpenArchiveModal)}
          onAccept={handleArchiveItems}
        />
      )}
      {isOpenConfirmAddToCommonLibrary && (
        <ModalConfirm
          title="Add all steps to common library?"
          content="You can find all added steps in common library."
          isOpen={isOpenConfirmAddToCommonLibrary}
          confirmAction="Add"
          toggle={onCloseConfirmAddToCommonLibrary}
          onAccept={() =>
            addAllStepsOfProcessToCommonLibrary(selectedProcess?.id, onCloseConfirmAddToCommonLibrary, () => {
              searchStore.search()
            })
          }
        />
      )}
    </VStack>
  )
}

export default observer(SearchPage)
